import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { kpiEntity } from '@/config/entities/kpis';
import { createCRUDService } from '@/services/genericCRUD';
import { ContextualPanel, ContextualPanelItem } from '@/components/common/ContextualPanel';
import { 
  PencilIcon, 
  TrashIcon, 
  ArrowLeftIcon,
  PlusIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import { KpiTargetEntry } from '@/components/kpis/KpiTargetEntry';
import { ActiveStatusIcon } from '@/components/common/ActiveStatusIcon';

const kpiService = createCRUDService(kpiEntity);

export const KPIDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const [kpi, setKpi] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [transformations, setTransformations] = useState<any[]>([]);
  const [transformationsLoading, setTransformationsLoading] = useState(false);
  const [activePanel, setActivePanel] = useState<'transformations' | null>(null);

  useEffect(() => {
    // Search/nav can open the template path /kpis/:id — do not call the API with that token.
    if (id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
      loadKPI();
      loadTransformations();
    } else {
      setLoading(false);
      navigate('/kpis');
    }
  }, [id]);

  const loadKPI = async () => {
    try {
      setLoading(true);
      const data = await kpiService.getById(id!);
      setKpi(data);
    } catch (err) {
      toast.error('Failed to load KPI');
      navigate('/kpis');
    } finally {
      setLoading(false);
    }
  };

  const loadTransformations = async () => {
    try {
      setTransformationsLoading(true);
      const response = await api.get(`/kpis/${id}/transformations`);
      if (response.data.success) {
        setTransformations(response.data.data);
      }
    } catch (error) {
      console.error('Error loading transformations:', error);
      // Don't show error toast - transformations are optional
    } finally {
      setTransformationsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this KPI permanently? Targets and actuals for it are removed too. This cannot be undone.')) return;
    
    try {
      setDeleting(true);
      await kpiService.delete(id!);
      toast.success('KPI deleted successfully');
      navigate('/kpis');
    } catch (err) {
      toast.error('Failed to delete KPI');
    } finally {
      setDeleting(false);
    }
  };

  const renderFieldValue = (field: any, value: any) => {
    if (value === null || value === undefined || value === '') return '-';

    switch (field.type) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'number':
        return new Intl.NumberFormat().format(value);
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value);
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'datetime':
        return new Date(value).toLocaleString();
      default:
        return String(value);
    }
  };

  const getVisibleFields = () => {
    return kpiEntity.fields.filter(field => !field.hidden);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-[var(--color-secondary-text)]">Loading...</div>
        </div>
      </div>
    );
  }

  if (!kpi) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="text-[var(--color-secondary-text)]">{t('kpi.kpiNotFound')}</div>
        </div>
      </div>
    );
  }

  const visibleFields = getVisibleFields();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/kpis')}
            className="flex items-center space-x-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>{t('kpi.backToKPIs')}</span>
          </Button>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            onClick={() => navigate(`/performance/kpis/${id}`)}
          >
            <ChartBarIcon className="h-4 w-4 mr-2" />
            {t('kpi.historyView') || 'History'}
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                const response = await api.post(`/kpis/${id}/sync`);
                toast.success(response.data?.data?.message || t('kpi.syncDone'));
                loadKPI();
              } catch (error: any) {
                toast.error(error?.response?.data?.message || t('kpi.syncFailed'));
              }
            }}
          >
            {t('kpi.syncFromPlatform')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(`/kpis/${id}/edit`)}
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">{kpi.name}</h1>
        <p className="text-[var(--color-secondary-text)]">{t('kpi.kpiDetails')}</p>
      </div>

      <Card>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {visibleFields.map((field) => (
              <div key={field.key} className="space-y-2">
                <label className="block text-sm font-medium text-[var(--color-secondary-text)]">
                  {field.label}
                </label>
                <div className="text-sm text-[var(--color-secondary-text)]">
                  {field.key === 'status' ? (
                    <span className="inline-flex items-center gap-1.5">
                      <ActiveStatusIcon status={kpi.status} />
                      <span>
                        {(kpi.status || '').toUpperCase() === 'ACTIVE'
                          ? t('kpi.filterStatusActive') || 'Active'
                          : t('kpi.filterStatusInactive') || 'Inactive'}
                      </span>
                    </span>
                  ) : (
                    renderFieldValue(field, kpi[field.key || field.name || ''])
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="px-4 pt-3 text-sm font-medium text-[var(--color-surface-text)]">{t('kpi.addTarget')}</h2>
        {id && <KpiTargetEntry kpiId={id} />}
      </Card>

      {/* Related Transformations Section */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--color-secondary-text)]">
              Related Transformations
            </h2>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setActivePanel('transformations')}
              >
                View All ({transformations.length})
              </Button>
              <Button 
                size="sm" 
                onClick={() => navigate(`/transformations/create?kpi_id=${id}`)}
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Link Transformation
              </Button>
            </div>
          </div>
          {transformationsLoading ? (
            <div className="text-center py-4 text-[var(--color-secondary-text)] text-sm">Loading transformations...</div>
          ) : transformations.length === 0 ? (
            <div className="text-center py-4 text-[var(--color-secondary-text)] text-sm">No transformations linked to this KPI</div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {transformations.slice(0, 5).map((transformation) => (
                <div key={transformation.id} className="border border-[var(--color-border)] rounded p-2 text-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-[var(--color-surface-text)] text-xs">{transformation.name}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded ${
                          transformation.status === 'COMPLETED' ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]' :
                          transformation.status === 'IN_PROGRESS' ? 'bg-[var(--color-info)]/10 text-[var(--color-info)]' :
                          'bg-[var(--color-border)] text-[var(--color-secondary-text)]'
                        }`}>
                          {transformation.status}
                        </span>
                        {transformation.is_primary_kpi && (
                          <span className="px-1.5 py-0.5 text-xs rounded bg-[var(--color-info)]/10 text-[var(--color-info)]">
                            Primary
                          </span>
                        )}
                      </div>
                      {transformation.description && (
                        <div className="text-xs text-[var(--color-secondary-text)] mt-1 line-clamp-2">
                          {transformation.description}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Contextual Panel - Related Transformations */}
      <ContextualPanel
        title="Related Transformations"
        items={transformations.map((transformation): ContextualPanelItem => ({
          id: transformation.id,
          title: transformation.name,
          description: transformation.description,
          status: `${transformation.status}${transformation.is_primary_kpi ? ' - Primary KPI' : ''}`,
          metadata: transformation,
          onClick: () => {
            navigate(`/transformations/${transformation.id}`);
            setActivePanel(null);
          }
        }))}
        isLoading={transformationsLoading}
        emptyMessage="No transformations linked to this KPI"
        onCreateNew={() => {
          navigate(`/transformations/create?kpi_id=${id}`);
          setActivePanel(null);
        }}
        onCreateLabel="Link Transformation"
        onViewAll={() => {
          navigate(`/transformations?kpi_id=${id}`);
          setActivePanel(null);
        }}
        onViewAllLabel="View All Transformations"
        isOpen={activePanel === 'transformations'}
        onClose={() => setActivePanel(null)}
      />
    </div>
  );
}; 